// Job posting options and labels

export const contractTypes = [
  { value: 'determinato', labelKey: 'jobs.contractTypes.temporary' },
  { value: 'indeterminato', labelKey: 'jobs.contractTypes.permanent' },
  { value: 'partita_iva', labelKey: 'jobs.contractTypes.freelance' },
  { value: 'stage', labelKey: 'jobs.contractTypes.internship' },
  { value: 'apprendistato', labelKey: 'jobs.contractTypes.apprenticeship' },
] as const;

export const workModes = [
  { value: 'presenza', labelKey: 'jobs.workModes.onsite' },
  { value: 'remoto', labelKey: 'jobs.workModes.remote' },
  { value: 'ibrido', labelKey: 'jobs.workModes.hybrid' },
] as const;

export const salaryPeriods = [
  { value: 'annuale', labelKey: 'jobs.salaryPeriods.yearly' },
  { value: 'mensile', labelKey: 'jobs.salaryPeriods.monthly' },
  { value: 'orario', labelKey: 'jobs.salaryPeriods.hourly' },
] as const;

export const applicationMethods = [
  { value: 'external_link', labelKey: 'jobs.applicationMethods.external' },
  { value: 'email', labelKey: 'jobs.applicationMethods.email' },
  { value: 'upload_cv', labelKey: 'jobs.applicationMethods.platform' },
] as const;

export const inclusivityTags = [
  { value: 'lgbtq_friendly', labelKey: 'jobs.inclusivityTags.lgbtq_friendly', icon: '🏳️‍🌈' },
  { value: 'disability_friendly', labelKey: 'jobs.inclusivityTags.disability_friendly', icon: '♿' },
  { value: 'flexibility', labelKey: 'jobs.inclusivityTags.flexibility', icon: '⏰' },
  { value: 'pet_friendly', labelKey: 'jobs.inclusivityTags.pet_friendly', icon: '🐾' },
  { value: 'family_friendly', labelKey: 'jobs.inclusivityTags.family_friendly', icon: '👨‍👩‍👧' },
  { value: 'neurodiversity_friendly', labelKey: 'jobs.inclusivityTags.neurodiversity_friendly', icon: '🧠' },
  { value: 'cultural_diversity', labelKey: 'jobs.inclusivityTags.cultural_diversity', icon: '🌍' },
  { value: 'gender_equality', labelKey: 'jobs.inclusivityTags.gender_equality', icon: '⚖️' },
] as const;

export const inclusiveBenefits = [
  { value: 'flexible_hours', labelKey: 'jobs.benefits.flexible_hours', icon: '⏰' },
  { value: 'smart_working', labelKey: 'jobs.benefits.remote_work', icon: '🏠' },
  { value: 'parental_leave', labelKey: 'jobs.benefits.parental_leave', icon: '👶' },
  { value: 'health_insurance', labelKey: 'jobs.benefits.health_insurance', icon: '🏥' },
  { value: 'training', labelKey: 'jobs.benefits.diversity_training', icon: '📚' },
  { value: 'mentoring', labelKey: 'jobs.benefits.mental_health_support', icon: '🤝' },
  { value: 'pet_friendly', labelKey: 'jobs.benefits.pet_friendly', icon: '🐕' },
  { value: 'accessible_office', labelKey: 'jobs.benefits.accessibility_accommodations', icon: '🏢' },
] as const;

export type ContractType = typeof contractTypes[number]['value'];
export type WorkMode = typeof workModes[number]['value'];
export type SalaryPeriod = typeof salaryPeriods[number]['value'];
export type ApplicationMethod = typeof applicationMethods[number]['value'];
export type InclusivityTag = typeof inclusivityTags[number]['value'];
export type InclusiveBenefit = typeof inclusiveBenefits[number]['value'];

// Job posting interface
export interface JobPosting {
  id: string;
  company_id: string;
  title: string;
  slug: string;
  description: string;
  contract_type: ContractType;
  salary_range_min: number | null;
  salary_range_max: number | null;
  salary_period: SalaryPeriod;
  work_mode: WorkMode;
  location_city: string;
  location_region: string | null;
  required_skills: string[];
  nice_to_have_skills: string[];
  inclusive_benefits: string[];
  inclusivity_tags: string[];
  application_method: ApplicationMethod;
  application_url: string | null;
  application_email: string | null;
  status: 'draft' | 'active' | 'expired' | 'paused' | 'removed';
  is_extended: boolean;
  published_at: string | null;
  expires_at: string | null;
  total_clicks: number;
  unique_clicks: number;
  total_views: number;
  unique_views: number;
  created_at: string;
  updated_at: string;
  companies?: {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    is_verified: boolean;
  };
}

export interface JobApplication {
  id: string;
  job_posting_id: string;
  user_id: string | null;
  applicant_name: string;
  applicant_email: string;
  applicant_phone: string | null;
  cv_file_url: string | null;
  cover_letter: string | null;
  applied_at: string;
  ip_address: string | null;
}

export interface JobCredits {
  id: string;
  company_id: string;
  credits_available: number;
  credits_used: number;
  monthly_limit: number;
  last_reset_at: string;
  created_at: string;
}
